/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useEffect, useRef } from "react";
import {
  BlockStack,
  InlineStack,
  Modal,
  Checkbox,
  Badge,
  Card,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Property } from "../../types";
import { useTranslation } from "react-i18next";
import { TranslateLink } from "@shared/ui";

interface CreateFormModalProps {
  open: boolean;
  onClose: () => void;
  properties: Property[];
  clinicId: string;
}

const PropertyRow: React.FC<{
  property: Property;
  selectedFields: string[];
  setSelectedFields: React.Dispatch<React.SetStateAction<string[]>>;
}> = ({
  property,
  selectedFields,
  setSelectedFields,
}) => {
  const { t } = useTranslation();

  return (
    <SortableResourceItem
      accessibilityLabel={property.label}
      dragHandleAccessibilityLabel={`Reorder ${property.label}`}
    >
      <BlockStack gap="200">
        <Checkbox
          label={property.label}
          checked={selectedFields.includes(property.name)}
          onChange={(checked) => {
            const updatedFields = checked
              ? [...selectedFields, property.name]
              : selectedFields.filter((name) => name !== property.name);
            setSelectedFields(updatedFields);
          }}
        />
        <InlineStack gap="200">
          {property.isRequired && (
            <Badge>{t("createFormModal.mandatory")}</Badge>
          )}
          {property.isShortlisted && (
            <Badge>{t("createFormModal.shortlisted")}</Badge>
          )}
        </InlineStack>
      </BlockStack>
    </SortableResourceItem>
  );
};

const CreateFormModal: React.FC<CreateFormModalProps> = ({
  open,
  onClose,
  properties,
  clinicId,
}) => {
  const { PATCH } = useApiRequest();
  const queryClient = useQueryClient();
  const [selectedFields, setSelectedFields] = useState<string[]>(
    properties
      .filter((property) => property.isInCreateForm)
      .map((property) => property.name)
  );
  const [orderedProperties, setOrderedProperties] = useState<Property[]>([]);
  const hasInitializedForOpenRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) {
      hasInitializedForOpenRef.current = false;
      return;
    }

    if (hasInitializedForOpenRef.current) {
      return;
    }

    const initialSelectedFields = properties
      .filter((property) => property.isInCreateForm)
      .map((property) => property.name);
    const initialProperties = properties
      .filter(
        (property) => !property.isHiddenDefinition && !property.isReadOnlyValue
      )
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    setSelectedFields(initialSelectedFields);
    setOrderedProperties(initialProperties);
    hasInitializedForOpenRef.current = true;
  }, [open, properties]);

  const mutation = useMutation({
    mutationFn: (updatedProperties: any) =>
      PATCH(
        `/api/healthcare-companies/${clinicId}/patients/schema/properties/reorder-with-attributes`,
        {
          properties: updatedProperties,
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-schema"] });
      queryClient.invalidateQueries({
        queryKey: ["patient-schema-properties"],
      });
      onClose();
    },
    onError: (error) => {
      console.error("Failed to update properties:", error);
    },
  });

  const handleSave = () => {
    const updatedProperties = orderedProperties.map((property, index) => ({
      name: property.name,
      displayOrder: index + 1,
      isInCreateForm: selectedFields.includes(property.name),
    }));

    mutation.mutate(updatedProperties);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("createFormModal.title")}
      primaryAction={{
        content: t("createFormModal.saveButton"),
        onAction: handleSave,
        loading: mutation.isPending,
      }}
      secondaryActions={[
        {
          content: t("createFormModal.cancelButton"),
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <TranslateLink text={t("createFormModal.description")} />
          <BlockStack gap="200">
            <Card padding="0">
              <SortableList
                items={orderedProperties}
                idForItem={(property) => property.name}
                onChange={setOrderedProperties}
                renderItem={(property) => (
                  <PropertyRow
                    property={property}
                    selectedFields={selectedFields}
                    setSelectedFields={setSelectedFields}
                  />
                )}
              />
            </Card>
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default CreateFormModal;
